import { Component, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common'; 
import { FormsModule } from '@angular/forms';
import { GstInvoiceService } from '../../../../Services/Invoice/gst-invoice.service'; 
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { AuthService } from '../../../../Services/auth.service';
import { WhatsappInstanceService } from '../../../../Services/Whatsapp/whatsapp-instance.service';
import { NotificationService } from '../../../../Services/notification.service';

@Component({
  selector: 'app-gst-bill-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gst-bill-report.component.html',
  styleUrl: './gst-bill-report.component.css',
  providers: [DecimalPipe, DatePipe] 
})
export class GstBillReportComponent implements OnInit {
  allInvoices: any[] = [];        
  filteredInvoices: any[] = [];   
  paginatedInvoices: any[] = [];  
  
  searchTerm: string = '';
  startDate: string = ''; 
  endDate: string = '';   
  
  isLoading: boolean = false;
  totalGrandSum: number = 0;
  
  currentPage: number = 1;
  pageSize: number = 10;
  lastPage: number = 1;
  totalItems: number = 0;

  constructor(
    private gstService: GstInvoiceService, 
    private router: Router,
    private datePipe: DatePipe,
    private authService:AuthService,
    private whatsappInstanceService:WhatsappInstanceService,
    private notification:NotificationService 
  ) {}

  ngOnInit() {
    this.loadInitialData();
  }

  loadInitialData() {
    this.isLoading = true;
    this.gstService.getGstInvoices().subscribe({
      next: (res: any) => {
        this.allInvoices = res.data.reverse() || [];
        this.applyFilter(); 
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
      }
    });
  }

  onSearch() {
    this.currentPage = 1; 
    this.applyFilter();
  }

  applyFilter() {
    const search = this.searchTerm.toLowerCase().trim();
    
    this.filteredInvoices = this.allInvoices.filter(inv => {
      // 1. Search Logic
      const matchesSearch = !search || (
        inv.customer_name?.toLowerCase().includes(search) ||
        inv.invoice_no?.toLowerCase().includes(search) ||
        inv.customer_gstin?.toLowerCase().includes(search) ||
        inv.customer_pan?.toLowerCase().includes(search) ||
        inv.customer_mobile?.includes(search)
      );

      // 2. Date Range Logic
      const invDate = new Date(inv.created_at).setHours(0, 0, 0, 0);
      const start = this.startDate ? new Date(this.startDate).setHours(0, 0, 0, 0) : null;
      const end = this.endDate ? new Date(this.endDate).setHours(0, 0, 0, 0) : null;

      let matchesDate = true;
      if (start && invDate < start) matchesDate = false;
      if (end && invDate > end) matchesDate = false;

      return matchesSearch && matchesDate;
    });

    this.totalItems = this.filteredInvoices.length;
    this.lastPage = Math.ceil(this.totalItems / this.pageSize) || 1;
    this.calculateTotals();
    this.updatePageData();
  }

  updatePageData() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedInvoices = this.filteredInvoices.slice(startIndex, endIndex);
  }

  calculateTotals() {
    this.totalGrandSum = this.filteredInvoices.reduce((acc, curr) => 
      acc + parseFloat(curr.grand_total || 0), 0
    );
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.lastPage) {
      this.currentPage = page;
      this.updatePageData();
    }
  }

  formatExcelDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    return `${day} - ${months[date.getMonth()]} - ${date.getFullYear()}`;
  }

  exportToExcel() {
    // 1. Prepare Header Info
    const reportPeriod = (this.startDate && this.endDate) 
      ? `${this.formatExcelDate(this.startDate)} to ${this.formatExcelDate(this.endDate)}` 
      : 'Full History';

    // 2. Map Filtered Table Data
    const dataToExport = this.filteredInvoices.map(inv => ({
      'Date': this.formatExcelDate(inv.created_at),
      'Invoice No': inv.invoice_no,
      'Customer Name': inv.customer_name,
      'GSTIN': inv.customer_gstin,
      'PAN': inv.customer_pan,
      'Mobile': inv.customer_mobile,
      'Quantity': parseFloat(inv.quantity_per_gm || 0),
      'Place of suppliy': inv.supply_place,
      'Taxable Amt': parseFloat(inv.amount || 0),
      'CGST (1.5%)': parseFloat(inv.cgst_amount || 0),
      'SGST (1.5%)': parseFloat(inv.sgst_amount || 0),
      'IGST (3%)': parseFloat(inv.igst_amount || 0),
      'Discount': parseFloat(inv.discount || 0),
      'Grand Total': parseFloat(inv.grand_total || 0),
    }));

    // 3. Initialize Worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // 4. Add Professional Header Rows (Rows 1-5)
    XLSX.utils.sheet_add_aoa(worksheet, [
      ["MEENA SILVER & CO - GST BILL REPORT"],
      [`REPORT PERIOD: ${reportPeriod}`],
      [`TOTAL CONSOLIDATED AMOUNT: ₹ ${this.totalGrandSum.toFixed(2)}`],
      [`TOTAL INVOICE COUNT: ${this.totalItems}`],
      [] // Spacer
    ], { origin: "A1" });

    // 5. Add Table Data (Starting from Row 6)
    XLSX.utils.sheet_add_json(worksheet, dataToExport, { origin: "A6", skipHeader: false });

    // 6. Add Bottom Summary Row
    const lastRow = dataToExport.length + 7; 
    XLSX.utils.sheet_add_aoa(worksheet, [[
      '', '', '', '', '', '', '', '', '', '','', '', 'TOTAL SUM:', this.totalGrandSum
    ]], { origin: `A${lastRow}` });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Report');
    XLSX.writeFile(workbook, `GST_Report_${this.startDate || 'all'}.xlsx`);
  }

  printInvoice(id: any) {
    if(this.authService.isCA()){
      this.router.navigate(['/gst-invoice', id]);
    }else{
      this.router.navigate(['/invoice-gst', id]);
    }
  }

  deleteInvoice(id: any) {
    if (confirm('Delete this record permanently?')) {
      this.gstService.deleteGstInvoice(id).subscribe(() => {
        this.allInvoices = this.allInvoices.filter(i => i.id !== id);
        this.applyFilter();
      });
    }
  }

  sendPDFToWhatsapp(mobile:number,name:string){
    this.isLoading = true;

    this.whatsappInstanceService.sendWhatsappMsg({
      mobile: mobile,
      message: `Hello `+ name + ` Sir ` + `We are currently working on the feature to send GST report PDFs. This functionality is in progress and will be launched soon. Thank you for your patience.`
    }).subscribe({
      next: (res: any) => {
        console.log('📨 Message Sent:', res);

        if (!res?.success) {
          this.notification.showError('Message failed', 'WhatsApp');
        } else {
          this.notification.showSuccess('Message sent', 'WhatsApp');
        }

        this.isLoading = false;
      },
      error: () => {
        this.notification.showError('Message failed', 'WhatsApp');
        this.isLoading = false;
      }
    });
  }
}